import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileCheck } from 'lucide-react';
import Badge from '@components/common/Badge';
import { RZTable } from '@components/common/table';
import { StatusCell, AssessorCell } from '@components/common/table';
import { Assessment, TableColumn } from '@app-types';
import { formatAssessmentId } from '@utils/exportUtils';
import { usePermissions } from '@contexts/AuthContext';
import { hasOpenConflicts, getTierConflictTooltip } from '@utils/conflictUtils';
import { statusToVariant } from '@utils/badgeMappers';
import { formatDateOnly } from '@utils/dateUtils';
import { InfoCard, ProgressBar } from '@components/common';

interface SupplierAssessmentsProps {
  assessments: Assessment[];
  isLoading?: boolean;
  onViewAssessment?: (assessmentId: number) => void;
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
  };
}

// Column configuration
const assessmentColumns: TableColumn[] = [
  { key: 'assessment_id', label: 'Assessment #', defaultVisible: true },
  { key: 'name', label: 'Name', defaultVisible: true },
  { key: 'contract', label: 'Contract', defaultVisible: true },
  { key: 'status', label: 'Status', defaultVisible: true },
  { key: 'assessor', label: 'Assessor', defaultVisible: true },
  { key: 'scheduled_date', label: 'Scheduled Date', defaultVisible: true },
  { key: 'progress', label: 'Progress', defaultVisible: true }
];

const SupplierAssessments: React.FC<SupplierAssessmentsProps> = ({
  assessments,
  isLoading = false,
  onViewAssessment,
  pagination
}) => {
  const navigate = useNavigate();
  const permissions = usePermissions();

  const renderCellContent = (column: TableColumn, assessment: Assessment) => {
    switch (column.key) {
      case 'assessment_id':
        return (
          <button className="table-link" onClick={() => onViewAssessment?.(assessment.assessment_id)}>
            {formatAssessmentId(assessment.assessment_id)}
          </button>
        );

      case 'name':
        return <div>{assessment.assessment_name}</div>;

      case 'contract':
        return permissions.canAccessContracts && assessment.contract_id ? (
          <button className="table-link" onClick={() => navigate(`/contracts/${assessment.contract_id}`)}>
            {assessment.contract_number}
          </button>
        ) : (
          <span>{assessment.contract_number || 'N/A'}</span>
        );

      case 'status':
        return (
          <StatusCell
            hasConflict={hasOpenConflicts(assessment)}
            conflictTooltip={hasOpenConflicts(assessment) ? getTierConflictTooltip(assessment) : undefined}
          >
            <Badge variant={statusToVariant(assessment.assessment_status)}>{assessment.assessment_status}</Badge>
          </StatusCell>
        );

      case 'assessor':
        return <AssessorCell assessorName={assessment.assessor_name} assessorEmail={assessment.assessor_email} />;

      case 'scheduled_date':
        return formatDateOnly(assessment.scheduled_date, 'Not scheduled');

      case 'progress':
        return <ProgressBar percentage={assessment.progress_percentage} />;

      default: {
        const value = assessment[column.key as keyof Assessment];
        // Don't render complex objects like conflicts array
        if (typeof value === 'object') return '-';
        return value || '-';
      }
    }
  };

  return (
    <InfoCard>
      <InfoCard.Header>
        <InfoCard.Title icon={<FileCheck size={18} />}>
          Assessments {pagination ? `(${pagination.totalItems})` : `(${assessments.length})`}
        </InfoCard.Title>
      </InfoCard.Header>

      <InfoCard.Content>
        <RZTable
          data={assessments}
          columns={assessmentColumns}
          getRowKey={(assessment) => assessment.assessment_id}
          renderCell={renderCellContent}
          rowClassName={(assessment) => (hasOpenConflicts(assessment) ? 'has-conflict' : '')}
          pagination={
            pagination
              ? {
                  ...pagination,
                  itemName: 'assessments',
                  pageSizeOptions: [25, 50, 100]
                }
              : undefined
          }
          state={{
            isLoading,
            isEmpty: assessments.length === 0,
            loadingMessage: 'Loading assessments...'
          }}
          emptyState={<RZTable.Empty icon={<FileCheck size={48} />} title="No assessments found for this supplier." />}
        />
      </InfoCard.Content>
    </InfoCard>
  );
};

export default SupplierAssessments;
